import {useContext, useEffect, useMemo, useRef, useState} from 'react'
import {Flex, Form, Modal, Row} from 'antd'
import {ExclamationCircleOutlined} from '@ant-design/icons'
import {useIntl} from 'react-intl'
import {createSearchParams} from 'react-router-dom'
import {deleteRequest, getRequest} from '../../../services/base'
import {NavContext} from '../../../context/global_context'
import useDebouncedValue from '../../../utils/common_function'
import {buildPageUrl, downloadFileBlob, getDeleteTargetPage} from '../../../utils/common'
import {getSessionStorageData} from '../../../utils/SessionStorage'
import InputText from '../../common/InputText'
import Button from '../../common/Button'
import {CustomerContext, SELECT_CUSTOMER, SET_CUSTOMER_LIST,} from './context/context'
import CustomerTable from './CustomerTable'
import CustomerForm from './form'
import { notifyError } from '../../../utils/notifications'

const Customer = () => {
  const intl = useIntl()
  const [state, dispatch] = useContext(CustomerContext)
  const [, navDispatch] = useContext(NavContext)

  const [search, setSearch] = useState('')
  const [pagination, setPagination] = useState({})
  const currentUrl = useRef('/api/customer/')

  const [modal, contextHolder] = Modal.useModal()
  const debouncedSearch = useDebouncedValue(search, 500)

  const vehicle_redirect_url = getSessionStorageData('vehicle')?.search
    ? `/vehicles/?${createSearchParams(getSessionStorageData('vehicle'))}`
    : `/vehicles`

  useEffect(() => {
    navDispatch({
      type: 'header',
      data: {
        header: intl.formatMessage({ id: 'layout.customerSetup' }),
        sub_header: intl.formatMessage({ id: 'customer.subHeader' }),
        id: '',
        show_back_btn: true,
        dtc_details: {},
        back_btn_url: vehicle_redirect_url,
        full_width_container: false,
      },
    })
  }, [])

  const getCustomerListData = async (url = null) => {
    navDispatch({
      type: 'loader',
      data: {
        caption: intl.formatMessage({ id: 'customer.fetchingData' }),
        showLoader: true,
      },
      notification: {},
    })

    currentUrl.current = url ? url : currentUrl.current
    let res = await getRequest(currentUrl.current)
    setPagination(res?.pagination || {})
    dispatch({ type: SET_CUSTOMER_LIST, data: res?.results || [] })

    navDispatch({
      type: 'loader',
      data: { showLoader: false },
      notification: {},
    })

    // A 'loader' action resets `notification`, so notify after it, not before.
    if (res?.status === false) {
      notifyError(navDispatch, intl.formatMessage({ id: 'customer.loadError' }))
    }
  }

  useEffect(() => {
    getCustomerListData()
  }, [])

  useEffect(() => {
    const url = `/api/customer/?search=${search}`
    currentUrl.current = url
    getCustomerListData()
  }, [debouncedSearch])

  const deleteCustomer = async (record) => {
    let res = await deleteRequest(`/api/customer/${record.id}/`)
    if (res?.status) {
      navDispatch({
        type: 'notification',
        data: {
          notification_type: 'success',
          msg: intl.formatMessage({ id: 'customer.deleteSuccess' }),
        },
      })
      currentUrl.current = buildPageUrl(currentUrl.current, getDeleteTargetPage(pagination))
      getCustomerListData()
    } else {
      navDispatch({
        type: 'notification',
        data: {
          notification_type: 'error',
          msg: intl.formatMessage({ id: 'customer.deleteError' }),
        },
      })
    }
  }

  const confirmDelete = (record) => {
    modal.confirm({
      title: intl.formatMessage({ id: 'customer.confirmDelete' }),
      icon: <ExclamationCircleOutlined />,
      content: intl.formatMessage(
        { id: 'customer.confirmDeleteContent' },
        { name: record.name }
      ),
      onOk: () => deleteCustomer(record),
      centered: true,
      maskClosable: true,
    })
  }

  const onCreateCustomer = () => {
    dispatch({
      type: SELECT_CUSTOMER,
      data: { selected_customer: {}, show_customer_form: true },
    })
  }

  const onEditCustomer = (record) => {
    dispatch({
      type: SELECT_CUSTOMER,
      data: { selected_customer: record, show_customer_form: true },
    })
  }

  const exportCustomerJson = (record) => {
    modal.confirm({
      title: intl.formatMessage({ id: 'customer.confirmExport' }),
      icon: <ExclamationCircleOutlined />,
      content: intl.formatMessage(
        { id: 'customer.confirmExportContent' },
        { name: record.name }
      ),
      onOk: () => {
        const JSON_DATA = {
          make: record?.make,
          name: record?.name,
          business_timezone: record?.business_timezone,
          vin_dependent_make_model_tags:
            record?.vin_dependent_make_model_tags?.split(',') || [],
        }
        downloadFileBlob(
          new Blob([JSON.stringify(JSON_DATA)], {
            type: 'application/json',
          }),
          `${record?.make}_make.json`
        )
      },
      centered: true,
      maskClosable: true,
    })
  }

  const dataSource = useMemo(() => {
    if (!state?.customer_list?.length) return []
    return state.customer_list.map((item, index) => ({
      ...item,
      key: `customer-${index}`,
      rowKey: `customer-${item.id}`,
      index,
    }))
  }, [state.customer_list])

  return (
    <>
      {contextHolder}

      {state.show_customer_form && (
        <CustomerForm getCustomerListData={getCustomerListData} />
      )}

      <div className="page">
        <Row className="body">
          <Form className="full-width">
            <Flex justify="space-between" gap={16} className="full-width">
              <InputText
                name="search_customer"
                type="search"
                placeholder={intl.formatMessage({
                  id: 'customer.searchPlaceholder',
                })}
                value={search}
                isRequired={false}
                className="full-width"
                wrapperClass="full-width"
                onChange={(value) => setSearch(value)}
              />
              <Button onClick={onCreateCustomer}>
                {intl.formatMessage({ id: 'customer.createNew' })}
              </Button>
            </Flex>
          </Form>

          <Flex style={{ width: '100%' }}>
            <CustomerTable
              dataSource={dataSource}
              pagination={pagination}
              getCustomerListData={getCustomerListData}
              onEdit={onEditCustomer}
              onDelete={confirmDelete}
              onExportJson={exportCustomerJson}
            />
          </Flex>
        </Row>
      </div>
    </>
  )
}

export default Customer
